import { useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@apollo/client/react'
import { CheckCircle2, Loader2, TriangleAlert } from 'lucide-react'
import { Logo } from '@/features/marketing/components/Logo'
import { routes } from '@/shared/constants'
import { PortalBillingPaymentStatusDocument } from '@/graphql/generated/graphql'

/**
 * Página de retorno de la pasarela externa (`urlSuccess`/`urlError` del link,
 * pagos-zeus-3): /pago/retorno?billingId=…&resultado=exito|error. La de éxito
 * consulta el estado de la factura hasta verla PAID (la confirmación real
 * llega por el canal interno de Midas; la redirección solo orienta).
 */
export function PaymentReturnPage() {
  const [params] = useSearchParams()
  const billingId = Number(params.get('billingId') ?? 0)
  const ok = params.get('resultado') !== 'error'

  const { data, error } = useQuery(PortalBillingPaymentStatusDocument, {
    variables: { billingId },
    skip: !billingId || !ok,
    pollInterval: 5000,
    fetchPolicy: 'network-only',
  })
  const paid = data?.portalBilling?.status === 'PAID'

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="px-6 py-6">
        <Link to="/" aria-label="Inicio">
          <Logo />
        </Link>
      </header>
      <main className="grid place-items-center px-5 py-10">
        <div className="w-full max-w-md rounded-3xl border border-black/5 bg-white p-8 text-center shadow-sm">
          {!ok ? (
            <>
              <TriangleAlert className="mx-auto size-12 text-amber-500" />
              <h1 className="mt-3 text-xl font-bold">El pago no se completó</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                No te preocupes: tu factura sigue pendiente y puedes intentarlo
                de nuevo desde la sección de facturación.
              </p>
            </>
          ) : paid ? (
            <>
              <CheckCircle2 className="mx-auto size-12 text-emerald-600" />
              <h1 className="mt-3 text-xl font-bold">¡Pago confirmado!</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Tu factura quedó pagada y tu negocio se está activando. Ya
                puedes entrar a tu panel.
              </p>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto size-12 animate-spin text-primary" />
              <h1 className="mt-3 text-xl font-bold">Confirmando tu pago…</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {error
                  ? 'Inicia sesión para ver el estado de tu factura.'
                  : 'La pasarela nos confirma el pago en unos instantes. Puedes dejar esta página abierta.'}
              </p>
            </>
          )}
          <Link
            to={billingId ? routes.app.billing.detail(String(billingId)) : routes.app.dashboard}
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-foreground px-5 py-2.5 text-sm font-semibold text-background hover:bg-primary"
          >
            Ir a mi panel
          </Link>
        </div>
      </main>
    </div>
  )
}
